// The backend authenticates in one of two ways, depending on the environment:
// - "bearer" (UAT): user/verify_otp returns token { value, expiresAt }, which
//   we store and send as an Authorization header.
// - "cookie" (production): no token in the response; the backend sets a
//   cookie that the browser stores and sends by itself. We can't read it or
//   its expiry, so we rely on the backend's 401 to know the session ended.
// All of this lives in localStorage with the other session values, so the
// existing logout (localStorage.clear()) also removes it.

const TOKEN_KEY = "token";
const EXPIRES_KEY = "tokenExpiresAt";
const MODE_KEY = "authMode";
// Set before redirecting to login so the login page can explain why
const EXPIRED_FLAG = "sessionExpired";

// Call after the OTP is verified with the response's `token` (may be absent)
export function startSession(token) {
  if (token && token.value) {
    localStorage.setItem(MODE_KEY, "bearer");
    localStorage.setItem(TOKEN_KEY, token.value);
    localStorage.setItem(EXPIRES_KEY, token.expiresAt || "");
  } else {
    localStorage.setItem(MODE_KEY, "cookie");
  }
}

// Whether the user is logged in, as far as the browser can tell
export function hasSession() {
  const mode = localStorage.getItem(MODE_KEY);
  if (mode === "bearer") return getToken() !== null;
  return mode === "cookie";
}

function isExpired() {
  const expiresAt = Date.parse(localStorage.getItem(EXPIRES_KEY) || "");
  // Treat it as expired a few seconds early so a request doesn't land just after
  return !Number.isNaN(expiresAt) && Date.now() >= expiresAt - 5000;
}

// The current token, or null if there is none or it has expired
export function getToken() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token || isExpired()) return null;
  return token;
}

let redirecting = false;

// Clears the session and sends the user back to the login page. Safe to call
// from several failing requests at once.
export function endSession() {
  if (redirecting) return;
  redirecting = true;
  localStorage.clear();
  sessionStorage.setItem(EXPIRED_FLAG, "1");
  window.location.assign(process.env.BASE_URL || "/");
}

// True while the app is on its way back to the login page
export function isSessionEnding() {
  return redirecting;
}

// True once, right after endSession() sent the user to the login page
export function takeSessionExpiredFlag() {
  const expired = sessionStorage.getItem(EXPIRED_FLAG) === "1";
  sessionStorage.removeItem(EXPIRED_FLAG);
  return expired;
}
